import { useState } from "react";
import { toast } from "sonner";
import { Mail, Send, Loader2, CheckCircle2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";

const CATEGORIES = ["General", "Billing", "Bug report", "Feature request", "Account"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", category: "General", company: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.includes("@") || !form.message.trim()) {
      toast.error("Please add your name, a valid email and a message."); return;
    }
    setBusy(true);
    try {
      await api.contact(form);
      setSent(true);
      toast.success("Message sent successfully.");
    } catch (e) {
      toast.error(e?.response?.status === 429 ? "Too many messages — please try again later." : "Could not send. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#0A0A0A] text-white" data-testid="contact-page">
      <Navbar />
      <div className="mx-auto w-full max-w-2xl flex-1 px-5 py-14">
        <div className="mb-8">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-[#00F0FF]"><Mail size={13} /> Contact us</div>
          <h1 className="font-display text-4xl font-black tracking-tight sm:text-5xl">Get in touch</h1>
          <p className="mt-3 text-base text-[#A1A1AA]">Questions, billing help or feedback — we usually reply within 1–2 business days.</p>
        </div>

        {sent ? (
          <div className="rounded-2xl border border-[#22c55e]/30 bg-[#22c55e]/5 p-8 text-center" data-testid="contact-success">
            <CheckCircle2 className="mx-auto mb-3 text-[#22c55e]" size={36} />
            <div className="font-display text-xl font-bold">Message sent successfully</div>
            <p className="mt-2 text-sm text-[#A1A1AA]">Thanks for reaching out — we'll get back to you soon.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <input type="text" tabIndex={-1} autoComplete="off" value={form.company} onChange={(e) => set("company", e.target.value)} className="hidden" aria-hidden />
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block"><span className="mb-1.5 block text-xs uppercase tracking-widest text-[#A1A1AA]">Name</span>
                <input data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm outline-none focus:border-[#00F0FF]/50" /></label>
              <label className="block"><span className="mb-1.5 block text-xs uppercase tracking-widest text-[#A1A1AA]">Email</span>
                <input data-testid="contact-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm outline-none focus:border-[#00F0FF]/50" /></label>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block"><span className="mb-1.5 block text-xs uppercase tracking-widest text-[#A1A1AA]">Category</span>
                <select data-testid="contact-category" value={form.category} onChange={(e) => set("category", e.target.value)} className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm outline-none focus:border-[#00F0FF]/50">
                  {CATEGORIES.map((c) => <option key={c} value={c} className="bg-[#141414]">{c}</option>)}
                </select></label>
              <label className="block"><span className="mb-1.5 block text-xs uppercase tracking-widest text-[#A1A1AA]">Subject</span>
                <input data-testid="contact-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm outline-none focus:border-[#00F0FF]/50" /></label>
            </div>
            <label className="block"><span className="mb-1.5 block text-xs uppercase tracking-widest text-[#A1A1AA]">Message</span>
              <textarea data-testid="contact-message" rows={6} value={form.message} onChange={(e) => set("message", e.target.value)} className="w-full resize-none rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm outline-none focus:border-[#00F0FF]/50" /></label>
            <button type="submit" disabled={busy} data-testid="contact-submit" className="flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-black transition-colors hover:bg-white/90 disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send message
            </button>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}
